import { Link } from "react-router-dom";
import { useMemo } from "react";
import { Clock, Bike, BadgeCheck, Heart } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useLocation as useSavedLocation } from "@/context/LocationContext";
import { useFavorites } from "@/context/FavoritesContext";
import { SAUDI_CITIES, findCity } from "@/data/saudi_locations";
import { BRAND_LOGOS } from "@/data/brand_logos";

// Haversine distance in km
function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Pick nearest city (from restaurant's cities list) to the user
function nearestCityCoords(cities, userLat, userLng) {
  let best = null;
  let min = Infinity;
  for (const cid of cities || []) {
    const c = findCity(cid);
    if (!c) continue;
    const d = distanceKm(userLat, userLng, c.lat, c.lng);
    if (d < min) { min = d; best = c; }
  }
  return best ? { lat: best.lat, lng: best.lng, dist: min } : null;
}

// Colored initials SVG fallback
function initialsAvatar(name) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const colors = ["#E05D3A", "#00A85A", "#FFC107", "#1A1A1A", "#3A7CA5", "#8E4585"];
  const color = colors[(initial.charCodeAt(0) || 0) % colors.length];
  return `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'><rect width='80' height='80' rx='16' fill='${color}'/><text x='50%' y='55%' text-anchor='middle' font-family='Arial' font-size='40' font-weight='800' fill='white'>${initial}</text></svg>`
  )}`;
}

export default function RestaurantCard({ r }) {
  const { lang } = useLang();
  const { location } = useSavedLocation();
  const { isFavorite, toggleFavorite } = useFavorites();
  const name = lang === "ar" ? r.name_ar : r.name_en;
  const cuisine = lang === "ar" ? r.cuisine_ar : r.cuisine_en;
  const fav = isFavorite(r.id);

  const distance = useMemo(() => {
    const ncc = nearestCityCoords(r.cities, location.lat, location.lng);
    if (!ncc) return null;
    // Stable pseudo-random km offset per restaurant so each card shows a slightly different km
    const seed = r.id.split("").reduce((s, ch) => s + ch.charCodeAt(0), 0);
    const offset = ((seed % 50) / 10); // 0.0 – 4.9
    return Math.max(0.1, ncc.dist + offset);
  }, [r.cities, r.id, location.lat, location.lng]);

  const freeDelivery = r.delivery_fee <= 8;
  const logo = BRAND_LOGOS[r.id] || r.image;

  return (
    <Link
      to={`/restaurant/${r.id}`}
      className="relative block bg-white rounded-2xl border border-[#EAEAEA] hover:border-[#E05D3A]/40 hover:shadow-sm active:scale-[0.99] transition-all"
      data-testid={`restaurant-card-${r.id}`}
    >
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(r.id); }}
        className="absolute top-2.5 end-2.5 z-10 w-8 h-8 rounded-full bg-white/95 border border-[#EEE] flex items-center justify-center shadow-sm active:scale-90 transition-transform"
        aria-label={fav ? (lang === "ar" ? "إزالة من المفضلة" : "Remove favorite") : (lang === "ar" ? "إضافة للمفضلة" : "Add favorite")}
        data-testid={`favorite-toggle-${r.id}`}
      >
        <Heart
          className={`w-4 h-4 transition-colors ${fav ? "text-[#E05D3A] fill-[#E05D3A]" : "text-[#9A9A9A]"}`}
          strokeWidth={2.2}
        />
      </button>
      <div className="flex items-stretch gap-3 p-3">
        {/* Logo square */}
        <div className="w-[108px] h-[108px] rounded-2xl bg-white border border-[#F0F0F0] flex-shrink-0 overflow-hidden flex items-center justify-center">
          <img
            src={logo}
            alt={name}
            className="w-full h-full object-contain object-center"
            loading="lazy"
            onError={(e) => {
              if (e.currentTarget.src !== r.image && r.image) {
                e.currentTarget.src = r.image;
              } else {
                e.currentTarget.onerror = null;
                e.currentTarget.src = initialsAvatar(name);
              }
            }}
          />
        </div>

        {/* Info column */}
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="flex items-center gap-1.5">
            <h3 className="font-bold text-[15px] text-[#1A1A1A] truncate">{name}</h3>
            {r.rating >= 4.5 && <BadgeCheck className="w-4 h-4 text-[#E05D3A] fill-[#FFF1EA] flex-shrink-0" />}
          </div>

          <div className="flex items-center gap-2 mt-1.5 text-[12.5px] text-[#6B6B6B]">
            {distance != null && (
              <>
                <span className="bg-[#F5F5F5] rounded-full px-2 py-0.5 font-semibold text-[#1A1A1A] inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {distance.toFixed(1)} {lang === "ar" ? "كم" : "km"}
                </span>
                <span className="text-[#BDBDBD]">|</span>
              </>
            )}
            <span className="truncate">{cuisine}</span>
          </div>

          {freeDelivery && (
            <div className="mt-2 inline-flex items-center gap-1.5 self-start bg-[#E8F7EF] text-[#00A85A] rounded-full px-2.5 py-1 text-[11.5px] font-bold">
              <Bike className="w-3.5 h-3.5" />
              {lang === "ar" ? "مجاني" : "Free"}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
